import { useState } from 'react';
import { Check, Unlock } from 'lucide-react';
import { TIER_PRICING, type Tier } from '@vcard/shared';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

export interface TierOverrideValue {
  tier: string | null;
  until: string | null;
  note: string | null;
}

/** True when an unlock is set and not past its end date. */
export function isOverrideActive(v: TierOverrideValue | null | undefined): boolean {
  if (!v || !v.tier) return false;
  return !v.until || new Date(v.until).getTime() > Date.now();
}

/** Short label such as "Business bis 31.12.2026" for badges. */
export function formatOverride(v: TierOverrideValue, locale = 'de-DE'): string {
  const label = TIER_PRICING[v.tier as Tier]?.label ?? v.tier ?? '';
  if (!v.until) return label;
  return `${label} · ${new Date(v.until).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })}`;
}

const SELECTABLE_TIERS: Tier[] = ['starter', 'business', 'enterprise'];

const inputCls = 'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const labelCls = 'block text-xs font-medium text-ink-muted mb-1';

function toDateInput(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '';
}

/**
 * Superadmin form: unlock a plan for an organisation or a single user, optionally until a date.
 * An unlock raises the effective plan for the unlocked organisation/user; it never lowers it.
 */
export function TierOverrideForm({
  kind,
  id,
  initial,
  onSaved,
}: {
  kind: 'organisation' | 'user';
  id: string;
  initial: TierOverrideValue;
  onSaved?: (value: TierOverrideValue) => void;
}) {
  const { t } = useAppTranslation();
  const [tier, setTier] = useState(initial.tier ?? '');
  const [until, setUntil] = useState(toDateInput(initial.until));
  const [note, setNote] = useState(initial.note ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const expired = initial.tier && !isOverrideActive(initial);

  function addDays(days: number) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setUntil(d.toISOString().slice(0, 10));
  }

  async function save(nextTier: string | null) {
    setSaving(true);
    setMessage(null);
    const body = nextTier
      ? { tier: nextTier, until: until ? `${until}T23:59:59.000Z` : null, note: note.trim() || null }
      : { tier: null };
    try {
      const res = await fetch(`/api/admin/${kind === 'organisation' ? 'organisations' : 'users'}/${id}/tier-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; tierOverride?: string | null; tierOverrideUntil?: string | null; tierOverrideNote?: string | null };
      if (!res.ok) {
        setMessage({ type: 'error', text: data.error ?? `HTTP ${res.status}` });
        return;
      }
      const value = { tier: data.tierOverride ?? null, until: data.tierOverrideUntil ?? null, note: data.tierOverrideNote ?? null };
      if (!value.tier) { setTier(''); setUntil(''); setNote(''); }
      setMessage({ type: 'ok', text: value.tier ? t('admin.unlock.saved', 'Freischaltung gespeichert') : t('admin.unlock.removed', 'Freischaltung entfernt') });
      onSaved?.(value);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-muted">
        {kind === 'organisation'
          ? t('admin.unlock.hintOrg', 'Schaltet den gewählten Plan für alle Nutzer dieser Organisation frei, ohne dass bezahlt wird (z. B. zum Testen).')
          : t('admin.unlock.hintUser', 'Schaltet den gewählten Plan nur für diese Person frei, ohne dass bezahlt wird (z. B. zum Testen).')}
      </p>

      {expired && (
        <p className="rounded-lg border border-warning-500/30 bg-warning-100 dark:bg-warning-500/10 px-3 py-2 text-xs text-warning-700 dark:text-warning-300">
          {t('admin.unlock.expired', 'Die letzte Freischaltung ist abgelaufen:')} {formatOverride(initial)}
        </p>
      )}

      <div>
        <label htmlFor={`unlock-tier-${id}`} className={labelCls}>{t('admin.unlock.plan', 'Freigeschalteter Plan')}</label>
        <select id={`unlock-tier-${id}`} value={tier} onChange={(e) => setTier(e.target.value)} className={inputCls}>
          <option value="">{t('admin.unlock.none', 'Keine Freischaltung')}</option>
          {SELECTABLE_TIERS.map((tt) => (
            <option key={tt} value={tt}>{TIER_PRICING[tt].label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`unlock-until-${id}`} className={labelCls}>{t('admin.unlock.until', 'Gültig bis (leer = unbegrenzt)')}</label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id={`unlock-until-${id}`}
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className={`${inputCls} !w-auto`}
          />
          {[14, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => addDays(d)}
              className="rounded-full border border-line px-3 py-1 text-xs text-ink-muted hover:bg-raised hover:text-ink transition-colors"
            >
              +{d} {t('admin.unlock.days', 'Tage')}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor={`unlock-note-${id}`} className={labelCls}>{t('admin.unlock.note', 'Notiz (optional)')}</label>
        <input
          id={`unlock-note-${id}`}
          type="text"
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('admin.unlock.notePlaceholder', 'z. B. Testzugang Vertrieb')}
          className={inputCls}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={saving || !tier}
          onClick={() => save(tier)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          <Unlock className="h-4 w-4" aria-hidden="true" /> {t('admin.unlock.save', 'Freischalten')}
        </button>
        {isOverrideActive(initial) || initial.tier ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => save(null)}
            className="rounded-lg border border-line px-3 py-2 text-sm text-ink-muted hover:bg-raised hover:text-ink transition-colors"
          >
            {t('admin.unlock.remove', 'Freischaltung entfernen')}
          </button>
        ) : null}
        {message && (
          <span role="status" className={`inline-flex items-center gap-1 text-sm ${message.type === 'ok' ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-300'}`}>
            {message.type === 'ok' && <Check className="h-4 w-4" aria-hidden="true" />} {message.text}
          </span>
        )}
      </div>
    </div>
  );
}
